/**
 * Revenue over time — this period against the one before it.
 *
 * TWO SERIES, AND THEY ARE NOT THE SAME KIND OF THING.
 *   · the ghosted bar is the PRIOR period, shifted forward onto this period's
 *     axis by the server (`aligned_bucket`), so prior week 1 sits under current
 *     week 1 however the buckets happen to fall. Aligning it here, in the
 *     browser, would mean re-deriving the window and getting a different answer
 *     from the one the KPI deltas used.
 *   · the solid bar is THIS period, and the single best bucket in it is painted
 *     in the accent so the peak is findable without reading the axis.
 *
 * The per-type split (books / upgrades / extras) lives in the tooltip rather
 * than in a stack: a stacked bar cannot also carry a ghost twin and a
 * highlighted member without becoming unreadable, and the tier card beside this
 * one already answers "what was sold" in full.
 *
 * recharts is already a dependency. Colours come from the MUI theme and
 * brandColors — no hex literal in this file — so the chart tracks light/dark
 * like everything else.
 */

import React from 'react';
import { Box, Card, CardContent, Stack, Typography, useTheme } from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { alpha } from '@mui/material/styles';
import { brandColors } from '../../../theme/brandColors';
import { centsToUsd, num } from '../weddingFormat';
import type { RevenuePriorTrendPoint, RevenueTrendPoint } from '../weddingApi';

const TYPE_LABEL: Record<string, string> = {
  tier: 'Books',
  upgrade: 'Upgrades',
  extra_contributors: 'Extra contributors',
};

interface PivotRow {
  bucket: string;
  label: string;
  current: number;
  prior: number;
  tier: number;
  upgrade: number;
  extra_contributors: number;
  best: boolean;
}

function bucketLabel(key: string, bucket: string): string {
  const d = new Date(key);
  if (Number.isNaN(d.getTime())) return key;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    ...(bucket === 'month' ? { year: '2-digit' } : { day: 'numeric' }),
  });
}

function pivot(
  points: RevenueTrendPoint[],
  prior: RevenuePriorTrendPoint[],
  bucket: string,
  bestBucket: string | null
): PivotRow[] {
  const byBucket = new Map<string, PivotRow>();

  const ensure = (key: string): PivotRow => {
    if (!byBucket.has(key)) {
      byBucket.set(key, {
        bucket: key,
        label: bucketLabel(key, bucket),
        current: 0,
        prior: 0,
        tier: 0,
        upgrade: 0,
        extra_contributors: 0,
        best: false,
      });
    }
    return byBucket.get(key) as PivotRow;
  };

  for (const point of points || []) {
    const key = String(point.bucket);
    const row = ensure(key);
    const dollars = num(point.cents) / 100;
    row.current += dollars;
    if (point.purchase_type === 'tier') row.tier += dollars;
    else if (point.purchase_type === 'upgrade') row.upgrade += dollars;
    else row.extra_contributors += dollars;
  }

  // The prior period may land on buckets the current period never reached — a
  // strong week last quarter with nothing this quarter is exactly the shape an
  // operator needs to see, so those buckets are created rather than dropped.
  for (const point of prior || []) {
    const key = String(point.aligned_bucket);
    ensure(key).prior += num(point.cents) / 100;
  }

  if (bestBucket) {
    const row = byBucket.get(bestBucket);
    if (row) row.best = true;
  }

  return [...byBucket.values()].sort((a, b) => a.bucket.localeCompare(b.bucket));
}

interface Props {
  points: RevenueTrendPoint[];
  bucket: string;
  priorPoints?: RevenuePriorTrendPoint[];
  bestBucket?: { bucket: string; cents: import('../weddingApi').Numeric } | null;
}

const RevenueTrendChart: React.FC<Props> = ({ points, bucket, priorPoints, bestBucket }) => {
  const theme = useTheme();
  const rows = React.useMemo(
    () => pivot(points, priorPoints || [], bucket, bestBucket ? String(bestBucket.bucket) : null),
    [points, priorPoints, bucket, bestBucket]
  );

  const currentColor = brandColors.deepTeal;
  const bestColor = brandColors.warmGold;
  const priorColor = alpha(theme.palette.text.secondary, 0.28);

  const best = rows.find((r) => r.best);

  return (
    <Card sx={{ borderRadius: '16px', height: '100%' }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Revenue over time
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Paid purchases only, by {bucket}.
        </Typography>

        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No paid purchases in this range.
          </Typography>
        ) : (
          <>
            <Box sx={{ height: 320, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rows}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="label" stroke={theme.palette.text.secondary} fontSize={12} />
                  <YAxis
                    stroke={theme.palette.text.secondary}
                    fontSize={12}
                    tickFormatter={(v: number) => `$${v.toLocaleString()}`}
                  />
                  <Tooltip
                    formatter={(value, name) => [
                      centsToUsd(Math.round(Number(value) * 100)),
                      name === 'prior'
                        ? 'Prior period'
                        : name === 'current'
                          ? 'This period'
                          : TYPE_LABEL[String(name)] || String(name),
                    ]}
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      color: theme.palette.text.primary,
                    }}
                  />
                  <Legend
                    formatter={(name: string) =>
                      (name === 'prior' ? 'Prior period' : 'This period')}
                  />
                  <Bar dataKey="prior" fill={priorColor} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="current" radius={[3, 3, 0, 0]}>
                    {rows.map((row) => (
                      <Cell key={row.bucket} fill={row.best ? bestColor : currentColor} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>

            {/*
              A legend entry for the accent, because a colour with no key is a
              decoration. It names the bucket AND its total, so the highlight is
              readable even where the chart itself is not (screen reader, print).
            */}
            {best && bestBucket && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                <Box
                  aria-hidden
                  sx={{ width: 10, height: 10, borderRadius: '3px', backgroundColor: bestColor }}
                />
                <Typography variant="caption" color="text.secondary">
                  Best {bucket} — {best.label}, {centsToUsd(bestBucket.cents)}
                </Typography>
              </Stack>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default RevenueTrendChart;
